import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Shield,
  AlertTriangle,
  RefreshCw,
  Trophy,
  Ban,
  Clock,
  ChevronDown,
  ChevronUp,
  Mail,
  Ticket,
  Copy,
  Check,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { api, type StuckWager, type WaitlistEntry, type InviteCodeEntry } from '@/lib/api';
import { useToast } from '@/components/ui/use-toast';

// ============================================
// Tabs
// ============================================

type AdminTab = 'wagers' | 'waitlist' | 'invites';

// ============================================
// Stuck Wagers (existing)
// ============================================

const statusColors: Record<string, string> = {
  locked: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  in_progress: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  pending_verification: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  ready_to_collect: 'bg-green-500/20 text-green-400 border-green-500/30',
  disputed: 'bg-red-500/20 text-red-400 border-red-500/30',
};

function timeSince(date: string | null): string {
  if (!date) return 'N/A';
  const diff = Date.now() - new Date(date).getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (hours > 24) return `${Math.floor(hours / 24)}d ${hours % 24}h ago`;
  if (hours > 0) return `${hours}h ${mins}m ago`;
  return `${mins}m ago`;
}

function StuckWagerCard({ wager, onAction }: { wager: StuckWager; onAction: () => void }) {
  const [expanded, setExpanded] = useState(false);
  const [winnerId, setWinnerId] = useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const forceSettleMutation = useMutation({
    mutationFn: (params: { wagerId: string; winnerId: string | null; dbOnly?: boolean }) =>
      api.forceSettle(params.wagerId, params.winnerId, params.dbOnly),
    onSuccess: (data) => {
      toast({
        title: 'Settlement executed',
        description: `Action: ${data.action}${data.txHash ? ` | TX: ${data.txHash.slice(0, 10)}...` : ''}`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin-stuck-wagers'] });
      onAction();
    },
    onError: (err: Error) => {
      toast({
        title: 'Settlement failed',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  return (
    <Card className="border-border/50 bg-card/50">
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-wrap items-center gap-3 min-w-0">
            <Badge className={`${statusColors[wager.status] || 'bg-muted text-muted-foreground'} border text-xs`}>
              {wager.status.replace(/_/g, ' ')}
            </Badge>
            <span className="font-mono text-xs text-muted-foreground truncate">
              {wager.id.slice(0, 8)}...
            </span>
            <span className="text-sm font-medium">{wager.gameCode}</span>
            <span className="text-sm font-bold text-primary">${wager.amount} USDC</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-muted-foreground hidden sm:inline">
              <Clock className="inline h-3 w-3 mr-1" />
              {timeSince(wager.lockedAt || wager.createdAt)}
            </span>
            <Button variant="ghost" size="sm" onClick={() => setExpanded(!expanded)}>
              {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {/* Players row */}
        <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
          <span>{wager.creatorEmail}</span>
          <span>vs</span>
          <span>{wager.opponentEmail || 'No opponent'}</span>
        </div>

        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 space-y-4 border-t border-border/50 pt-4">
            <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-2">
              <div>
                <span className="text-muted-foreground">Challenge ID:</span>
                <span className="ml-2 font-mono text-xs break-all">{wager.id}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Escrow ID:</span>
                <span className="ml-2 font-mono text-xs">{wager.escrowWagerId || 'None'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Created:</span>
                <span className="ml-2">{new Date(wager.createdAt).toLocaleString()}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Locked:</span>
                <span className="ml-2">
                  {wager.lockedAt ? new Date(wager.lockedAt).toLocaleString() : 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Match Deadline:</span>
                <span className="ml-2">
                  {wager.matchDeadline ? new Date(wager.matchDeadline).toLocaleString() : 'N/A'}
                </span>
              </div>
              <div>
                <span className="text-muted-foreground">Winner ID:</span>
                <span className="ml-2 font-mono text-xs">{wager.winnerId || 'Not set'}</span>
              </div>
            </div>

            {/* On-chain vs DB state comparison */}
            <div className="rounded-lg border border-border/50 p-3 space-y-2">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">On-Chain vs DB State</div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <span className="text-muted-foreground">DB Creator Funded:</span>
                  <span className={`ml-2 font-medium ${wager.creatorFunded ? 'text-green-400' : 'text-red-400'}`}>
                    {wager.creatorFunded ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Chain P1 Funded:</span>
                  <span className={`ml-2 font-medium ${wager.onChainPlayer1Funded ? 'text-green-400' : 'text-red-400'}`}>
                    {wager.onChainPlayer1Funded == null ? 'N/A' : wager.onChainPlayer1Funded ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">DB Opponent Funded:</span>
                  <span className={`ml-2 font-medium ${wager.opponentFunded ? 'text-green-400' : 'text-red-400'}`}>
                    {wager.opponentFunded ? 'Yes' : 'No'}
                  </span>
                </div>
                <div>
                  <span className="text-muted-foreground">Chain P2 Funded:</span>
                  <span className={`ml-2 font-medium ${wager.onChainPlayer2Funded ? 'text-green-400' : 'text-red-400'}`}>
                    {wager.onChainPlayer2Funded == null ? 'N/A' : wager.onChainPlayer2Funded ? 'Yes' : 'No'}
                  </span>
                </div>
              </div>
              <div className="text-sm">
                <span className="text-muted-foreground">On-Chain Status:</span>
                <Badge className="ml-2 text-xs" variant="outline">
                  {wager.onChainStatus || 'Not initialized'}
                </Badge>
                {wager.onChainStatus && wager.creatorFunded && !wager.onChainPlayer1Funded && (
                  <span className="ml-2 text-xs text-yellow-400">
                    Mismatch: DB says funded but chain says not
                  </span>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <div className="flex-1">
                <label className="text-xs text-muted-foreground mb-1 block">
                  Winner User ID (leave empty to refund both)
                </label>
                <Input
                  placeholder="UUID of winner (or empty for refund)"
                  value={winnerId}
                  onChange={(e) => setWinnerId(e.target.value)}
                  className="font-mono text-sm"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 border-green-500/30 text-green-400 hover:bg-green-500/10"
                  onClick={() =>
                    forceSettleMutation.mutate({
                      wagerId: wager.id,
                      winnerId: winnerId.trim() || null,
                    })
                  }
                  disabled={forceSettleMutation.isPending}
                >
                  {winnerId.trim() ? (
                    <>
                      <Trophy className="h-3 w-3" />
                      Declare Winner
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3" />
                      Refund On-Chain
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1 border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10"
                  onClick={() =>
                    forceSettleMutation.mutate({
                      wagerId: wager.id,
                      winnerId: winnerId.trim() || null,
                      dbOnly: true,
                    })
                  }
                  disabled={forceSettleMutation.isPending}
                >
                  <Ban className="h-3 w-3" />
                  {winnerId.trim() ? 'Settle DB Only' : 'Void DB Only'}
                </Button>
              </div>
            </div>

            {forceSettleMutation.isPending && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                Processing on-chain transaction...
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ============================================
// Waitlist Tab
// ============================================

function WaitlistTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');

  const { data: entries, isLoading } = useQuery({
    queryKey: ['admin-waitlist', statusFilter],
    queryFn: () => api.getWaitlistEntries({ status: statusFilter || undefined }),
    refetchInterval: 30000,
  });

  const inviteMutation = useMutation({
    mutationFn: (email: string) => api.generateInviteCode(email),
    onSuccess: (data) => {
      toast({
        title: 'Invite code generated',
        description: `Code: ${data.code} — send this to the user.`,
      });
      queryClient.invalidateQueries({ queryKey: ['admin-waitlist'] });
      queryClient.invalidateQueries({ queryKey: ['admin-invites'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed', description: err.message, variant: 'destructive' });
    },
  });

  const grantMutation = useMutation({
    mutationFn: (email: string) => api.grantUserAccess(email),
    onSuccess: () => {
      toast({ title: 'Access granted', description: 'User can now use the platform.' });
      queryClient.invalidateQueries({ queryKey: ['admin-waitlist'] });
      queryClient.invalidateQueries({ queryKey: ['admin-invites'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed', description: err.message, variant: 'destructive' });
    },
  });

  const waitlistEntries = (entries as WaitlistEntry[] | undefined) ?? [];

  const statusCounts = waitlistEntries.reduce<Record<string, number>>((acc, e) => {
    acc[e.status] = (acc[e.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold">{waitlistEntries.length}</div>
            <div className="text-xs text-muted-foreground">Total</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-amber-400">{statusCounts['pending'] ?? 0}</div>
            <div className="text-xs text-muted-foreground">Pending</div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <div className="text-2xl font-bold text-emerald-400">{statusCounts['invited'] ?? 0}</div>
            <div className="text-xs text-muted-foreground">Invited</div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {['', 'pending', 'invited', 'joined'].map((s) => (
          <Button
            key={s}
            variant={statusFilter === s ? 'default' : 'outline'}
            size="sm"
            onClick={() => setStatusFilter(s)}
          >
            {s || 'All'}
          </Button>
        ))}
      </div>

      {/* Entries list */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : waitlistEntries.length === 0 ? (
        <Card>
          <CardContent className="flex h-40 flex-col items-center justify-center text-center">
            <Mail className="mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No waitlist entries found.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {waitlistEntries.map((entry) => (
            <Card key={entry.id} className="border-border/50 bg-card/50">
              <CardContent className="flex items-center justify-between p-4">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{entry.email}</span>
                    <Badge
                      className={`text-xs border ${
                        entry.status === 'pending'
                          ? 'bg-amber-500/20 text-amber-400 border-amber-500/30'
                          : entry.status === 'invited'
                          ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
                          : 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30'
                      }`}
                    >
                      {entry.status}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {entry.xHandle && <span>@{entry.xHandle}</span>}
                    <span>{new Date(entry.createdAt).toLocaleDateString()}</span>
                  </div>
                </div>
                {entry.status !== 'joined' && (
                  <div className="flex gap-2 shrink-0">
                    {entry.status === 'pending' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="gap-1"
                        onClick={() => inviteMutation.mutate(entry.email)}
                        disabled={inviteMutation.isPending}
                      >
                        <Ticket className="h-3 w-3" />
                        Generate Code
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
                      onClick={() => grantMutation.mutate(entry.email)}
                      disabled={grantMutation.isPending}
                    >
                      <UserPlus className="h-3 w-3" />
                      Grant Access
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Invite Codes Tab
// ============================================

function InviteCodesTab() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [grantEmail, setGrantEmail] = useState('');

  const { data: codes, isLoading } = useQuery({
    queryKey: ['admin-invites'],
    queryFn: () => api.getInviteCodes(),
    refetchInterval: 30000,
  });

  const generateMutation = useMutation({
    mutationFn: () => api.generateInviteCode(),
    onSuccess: (data) => {
      toast({ title: 'Code generated', description: data.code });
      queryClient.invalidateQueries({ queryKey: ['admin-invites'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed', description: err.message, variant: 'destructive' });
    },
  });

  const grantMutation = useMutation({
    mutationFn: (email: string) => api.grantUserAccess(email),
    onSuccess: () => {
      toast({ title: 'Access granted' });
      setGrantEmail('');
      queryClient.invalidateQueries({ queryKey: ['admin-invites'] });
      queryClient.invalidateQueries({ queryKey: ['admin-waitlist'] });
    },
    onError: (err: Error) => {
      toast({ title: 'Failed', description: err.message, variant: 'destructive' });
    },
  });

  const copyCode = (code: string, id: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const inviteCodes = (codes as InviteCodeEntry[] | undefined) ?? [];

  return (
    <div className="space-y-4">
      {/* Actions row */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <Button
          variant="gradient"
          size="sm"
          className="gap-2"
          onClick={() => generateMutation.mutate()}
          disabled={generateMutation.isPending}
        >
          <Ticket className="h-4 w-4" />
          Generate Invite Code
        </Button>

        <div className="flex flex-1 gap-2">
          <Input
            type="email"
            placeholder="user@email.com — grant access directly"
            value={grantEmail}
            onChange={(e) => setGrantEmail(e.target.value)}
            className="text-sm"
          />
          <Button
            variant="outline"
            size="sm"
            className="gap-1 shrink-0"
            onClick={() => grantMutation.mutate(grantEmail.trim())}
            disabled={!grantEmail.trim() || grantMutation.isPending}
          >
            <UserPlus className="h-3 w-3" />
            Grant Access
          </Button>
        </div>
      </div>

      {/* Codes list */}
      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : inviteCodes.length === 0 ? (
        <Card>
          <CardContent className="flex h-40 flex-col items-center justify-center text-center">
            <Ticket className="mb-3 h-8 w-8 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">No invite codes generated yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {inviteCodes.map((code) => (
            <Card key={code.id} className="border-border/50 bg-card/50">
              <CardContent className="flex items-center justify-between p-4">
                <div className="min-w-0 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-sm font-bold text-primary">{code.code}</span>
                    <button
                      onClick={() => copyCode(code.code, code.id)}
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {copiedId === code.id ? (
                        <Check className="h-3.5 w-3.5 text-emerald-400" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </button>
                    {code.usedBy && (
                      <Badge className="text-xs border bg-emerald-500/20 text-emerald-400 border-emerald-500/30">
                        used
                      </Badge>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    {code.email && <span>{code.email}</span>}
                    <span>by {code.createdBy}</span>
                    <span>{new Date(code.createdAt).toLocaleDateString()}</span>
                    {code.usedBy && <span>Used by: {code.usedBy}</span>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Admin Page
// ============================================

export function AdminPage() {
  const [activeTab, setActiveTab] = useState<AdminTab>('wagers');

  const {
    data: stuckData,
    isLoading,
    isError,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['admin-stuck-wagers'],
    queryFn: () => api.getStuckWagers(),
    refetchInterval: 30000,
    retry: false,
  });

  // If API returns forbidden/unauthorized, show access denied
  if (isError) {
    return (
      <div className="flex h-[60vh] flex-col items-center justify-center text-center">
        <Shield className="mb-4 h-12 w-12 text-destructive" />
        <h2 className="text-xl font-bold">Access Denied</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {error?.message || 'You do not have admin privileges.'}
        </p>
      </div>
    );
  }

  const wagers = stuckData?.wagers ?? [];
  const byStatus = wagers.reduce<Record<string, StuckWager[]>>((acc, w) => {
    acc[w.status] = acc[w.status] || [];
    acc[w.status].push(w);
    return acc;
  }, {});

  const tabs: { id: AdminTab; label: string; icon: React.ReactNode }[] = [
    { id: 'wagers', label: 'Stuck Wagers', icon: <AlertTriangle className="h-4 w-4" /> },
    { id: 'waitlist', label: 'Waitlist', icon: <Mail className="h-4 w-4" /> },
    { id: 'invites', label: 'Invite Codes', icon: <Ticket className="h-4 w-4" /> },
  ];

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
            <Shield className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold sm:text-3xl">Admin Dashboard</h1>
            <p className="text-muted-foreground">Manage challenges, waitlist, and invites</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            {dataUpdatedAt ? `Updated ${new Date(dataUpdatedAt).toLocaleTimeString()}` : ''}
          </span>
          <Button variant="outline" size="sm" onClick={() => refetch()} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tab navigation */}
      <div className="flex gap-1 rounded-lg border border-border/40 bg-card/30 p-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-primary/10 text-primary'
                : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'wagers' && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold">{wagers.length}</div>
                <div className="text-xs text-muted-foreground">Total Stuck</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-yellow-400">
                  {byStatus['in_progress']?.length ?? 0}
                </div>
                <div className="text-xs text-muted-foreground">In Progress</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-purple-400">
                  {byStatus['pending_verification']?.length ?? 0}
                </div>
                <div className="text-xs text-muted-foreground">Pending Verify</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 text-center">
                <div className="text-2xl font-bold text-red-400">
                  {byStatus['disputed']?.length ?? 0}
                </div>
                <div className="text-xs text-muted-foreground">Disputed</div>
              </CardContent>
            </Card>
          </div>

          {/* Wager List */}
          {isLoading ? (
            <div className="flex h-60 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : wagers.length === 0 ? (
            <Card>
              <CardContent className="flex h-60 flex-col items-center justify-center text-center">
                <Shield className="mb-4 h-12 w-12 text-muted-foreground" />
                <h3 className="font-semibold">All clear</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  No stuck challenges found. Everything is running smoothly.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {wagers.map((wager) => (
                <StuckWagerCard key={wager.id} wager={wager} onAction={() => refetch()} />
              ))}
            </div>
          )}

          {/* Warning */}
          <Card className="border-yellow-500/20 bg-yellow-500/5">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-400">Admin Actions are Irreversible</p>
                  <p className="mt-1 text-muted-foreground">
                    Force-settling executes an on-chain transaction via the oracle wallet. Double-check
                    the winner ID before declaring. Use "Force Refund" to refund both players when a
                    match result is disputed or unclear.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 'waitlist' && <WaitlistTab />}
      {activeTab === 'invites' && <InviteCodesTab />}
    </div>
  );
}
